import {
  ExtendButtonBase,
  ListItem,
  ListItemButton,
  ListItemButtonTypeMap,
  ListItemProps,
} from '@mui/material';
import { SxProps, styled } from '@mui/material/styles';

import { px2rem } from '@/utils/px2rem';

type CustomProps = {
  shrink?: boolean;
  active?: boolean;
};

type MenuListItemButtonProps = ExtendButtonBase<
  ListItemButtonTypeMap<CustomProps>
>;

const omitProps = new Set(['active', 'shrink']);

const StyledButton = styled<MenuListItemButtonProps>(ListItemButton, {
  shouldForwardProp: (prop: string) => !omitProps.has(prop),
})(({ theme, active, shrink }) => ({
  backgroundColor: active
    ? theme.palette.primary.main
    : theme.palette.common.white,
  borderRadius: 12,
  padding: shrink ? 12 : '8px 16px',
  minHeight: '48px',
  gap: 8,
  color: active ? theme.palette.common.white : theme.palette.text.primary,
  flexDirection: shrink ? 'column' : 'row',
  justifyContent: shrink ? 'center' : 'initial',
  height: shrink ? 80 : 48,

  transition: 'height 0.15s ease-in-out',

  '& .MuiTypography-body1': {
    fontWeight: active
      ? theme.typography.fontWeightBold
      : theme.typography.fontWeightMedium,
    fontSize: px2rem(16),
    lineHeight: px2rem(20),
  },

  '& .MuiListItemText-root': {
    margin: 0,
  },

  '& .MuiSvgIcon-root': {
    color: active ? theme.palette.common.white : theme.palette.secondary.dark,
  },

  '&:hover': {
    backgroundColor: active
      ? theme.palette.primary.main
      : theme.palette.common.white,
  },
}));

type MenuListItemProps = ListItemProps &
  CustomProps & { buttonStyles?: SxProps };

export const MenuListItem = ({
  shrink,
  children,
  active,
  sx,
  buttonStyles,
  ...props
}: MenuListItemProps) => {
  return (
    <ListItem sx={{ padding: '8px 0', ...sx }} {...props}>
      <StyledButton shrink={shrink} active={active} sx={buttonStyles}>
        {children}
      </StyledButton>
    </ListItem>
  );
};
